// src/pages/Profile.js 
import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Logout from '@mui/icons-material/Logout';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import Orders from '../components/Orders/Orders';

const Profile = () => {
  const { user, signOut } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState({
    full_name: '',
    phone_number: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileAndOrders = async () => {
      try {
        // Fetch user profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        setProfileData({
          full_name: profile?.full_name || '',
          phone_number: profile?.phone_number || ''
        });

        // Fetch orders with their items, products, colors, and sizes
        const { data: ordersData, error: ordersError } = await supabase
  .from('orders')
  .select(`
    id,
    created_at,
    total_amount,
    payment_method,
    payment_screenshot,
    status,
    updated_at,
    order_items (
      id,
      quantity,
      price_at_purchase,
      size,
      name,
      color,
      image_url,
      product_id,
      product_color_id
    )
  `)
  .eq('user_id', user.id)
  .order('created_at', { ascending: false });

        if (ordersError) throw ordersError;
        
        setOrders(ordersData || []);
      } catch (error) {
        console.error('Profile error:', error);
        setError('حدث خطأ في تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchProfileAndOrders();
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', user.id);

      if (error) throw error;

      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: profileData.full_name,
          phone_number: profileData.phone_number
        }
      });

      if (authError) throw authError;

      setError('');
      alert('تم تحديث الملف الشخصي بنجاح');
    } catch (error) {
      console.error('Update error:', error);
      setError('حدث خطأ أثناء التحديث');
    }
  };

  const getOrderItemsSummary = (order) => {
    if (!order.order_items || order.order_items.length === 0) {
      return 'لا توجد منتجات';
    }

    // Get unique product names and their total quantities
    const productSummary = order.order_items.reduce((acc, item) => {
      // Use the name field directly from order_items table
      const productName = item.name || 'منتج غير معروف';
      
      if (!acc[productName]) {
        acc[productName] = 0;
      }
      acc[productName] += item.quantity;
      return acc;
    }, {});

    // Convert to string format
    return Object.entries(productSummary)
      .map(([name, qty]) => `${name} (${qty})`)
      .join(', ');
  };



  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );

  // Shared sx for aligning the outline notch and label
  const alignRightSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        transformOrigin: 'top right',
        right: 12,
        left: 'auto'
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Contact Information Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
          معلومات الاتصال
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Avatar
              src={user?.user_metadata?.avatar_url}
              sx={{
                width: 100,
                height: 100,
                border: '3px solid #2e7d32',
                margin: '0 auto'
              }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Box component="form" onSubmit={handleUpdateProfile} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="البريد الإلكتروني"
                    variant="outlined"
                    value={user?.email || ''}
                    InputProps={{
                      readOnly: true,
                      style: { 
                        backgroundColor: '#f5f5f5',
                        textAlign: 'right'
                      }
                    }}
                    InputLabelProps={{
                      sx: {
                        textAlign: 'right',
                        transformOrigin: 'top right',
                        right: 30,
                        left: 'auto'
                      }
                    }}
                    sx={alignRightSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="الاسم الكامل"
                    variant="outlined"
                    value={profileData.full_name}
                    onChange={(e) =>
                      setProfileData({ ...profileData, full_name: e.target.value })
                    }
                    InputProps={{ style: { textAlign: 'right' } }}
                    InputLabelProps={{
                      sx: {
                        textAlign: 'right',
                        transformOrigin: 'top right',
                        right: 30,
                        left: 'auto'
                      }
                    }}
                    sx={alignRightSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    variant="outlined"
                    value={profileData.phone_number}
                    onChange={(e) =>
                      setProfileData({ ...profileData, phone_number: e.target.value })
                    }
                    InputProps={{ style: { textAlign: 'right' } }}
                    InputLabelProps={{
                      sx: {
                        textAlign: 'right',
                        transformOrigin: 'top right',
                        right: 30,
                        left: 'auto'
                      }
                    }}
                    sx={alignRightSx}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ fontWeight: 'bold' }}
                >
                  حفظ التغييرات
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => signOut().then(() => navigate('/'))}
                  startIcon={<Logout />}
                  sx={{
                    fontWeight: 'bold',
                    '& .MuiButton-startIcon': {
                      marginRight: '1px',
                      marginLeft: 1
                    }
                  }}
                >
                  تسجيل الخروج
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Orders Section */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 2, 
        borderRadius: 1,
        mb: 3,
        mx: { xs: 0.5, md: 1 }, // Responsive negative horizontal margins
        mt: { xs: 1, md: 2 }  // Responsive negative top margin
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
          طلباتي
        </Typography>
      </Box>
        <Divider sx={{ mb: 2 }} />
        
        {orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ShoppingBag sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              لا توجد طلبات سابقة
            </Typography>
          </Box>
        ) : (
          <Orders 
            orders={orders}
            getOrderItemsSummary={getOrderItemsSummary}
          />
        )}
      </Paper>
    </Box>
  );
};

export default Profile;